'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { VariantPrompt } from '@/lib/types';
import VariantPreviewCard from './VariantPreviewCard';
import api from '@/lib/api';

interface VariantPreviewGridProps {
  prompts: VariantPrompt[];
  selectedIds: Set<number>;
  onToggleSelect: (promptId: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  isPolling: boolean;
  seriesId?: number | null;
}

export default function VariantPreviewGrid({
  prompts = [],
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  isPolling,
  seriesId,
}: VariantPreviewGridProps) {
  const router = useRouter();
  const safePrompts = prompts || [];
  const completedCount = safePrompts.filter(p => p.preview_status === 'completed').length;
  const processingCount = safePrompts.filter(p => p.preview_status === 'processing' || p.preview_status === 'pending').length;
  const failedCount = safePrompts.filter(p => p.preview_status === 'failed').length;

  // Detail view state
  const [detailIndex, setDetailIndex] = useState<number | null>(null);

  // Handle keyboard navigation
  useEffect(() => {
    if (detailIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDetailIndex(null);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (currentPrompt) {
          onToggleSelect(currentPrompt.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [detailIndex, safePrompts]);

  const goToPrevious = () => {
    if (detailIndex !== null && detailIndex > 0) {
      setDetailIndex(detailIndex - 1);
    }
  };

  const goToNext = () => {
    if (detailIndex !== null && detailIndex < safePrompts.length - 1) {
      setDetailIndex(detailIndex + 1);
    }
  };

  const currentPrompt = detailIndex !== null ? safePrompts[detailIndex] : null;

  // Detail View Modal
  if (currentPrompt && detailIndex !== null) {
    const isSelected = selectedIds.has(currentPrompt.id);
    const isCompleted = currentPrompt.preview_status === 'completed';

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={() => setDetailIndex(null)}
      >
        <div
          className="bg-[var(--bg-elevated)] rounded-xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden border border-[var(--border-subtle)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Up Arrow - Navigation */}
          <button
            onClick={goToPrevious}
            disabled={detailIndex === 0}
            className={`
              relative z-10 flex items-center justify-center py-4 border-b border-[var(--border-subtle)]
              transition-colors cursor-pointer
              ${detailIndex === 0
                ? 'text-[var(--text-muted)] opacity-30 cursor-not-allowed'
                : 'text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-primary)]'}
            `}
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
            <span className="ml-2 text-sm font-medium">Previous ({detailIndex}/{safePrompts.length})</span>
          </button>

          {/* Image Preview */}
          <div className="relative aspect-square bg-[var(--bg-primary)] flex items-center justify-center overflow-hidden">
            {currentPrompt.preview_path ? (
              <img
                src={api.getImageUrl(currentPrompt.preview_path)}
                alt="Preview"
                className="w-full h-full object-contain"
              />
            ) : currentPrompt.preview_status === 'failed' ? (
              <div className="flex flex-col items-center text-[var(--error)]">
                <svg className="w-16 h-16 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="mt-2 text-sm">Generation failed</span>
              </div>
            ) : (
              <div className="flex flex-col items-center text-[var(--text-muted)]">
                <svg className="w-16 h-16 animate-pulse opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="mt-2 text-sm">Generating...</span>
              </div>
            )}

            {/* Selection badge */}
            {isSelected && (
              <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center">
                <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>

          {/* Prompt Text & Actions */}
          <div className="px-6 py-4 border-t border-[var(--border-subtle)] space-y-4">
            {/* Prompt text */}
            <div className="max-h-32 overflow-y-auto">
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                {currentPrompt.prompt}
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setDetailIndex(null)}
                className="flex-1 px-4 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              >
                Back to Grid
              </button>
              <button
                onClick={() => onToggleSelect(currentPrompt.id)}
                disabled={!isCompleted}
                className={`flex-1 px-4 py-2 font-medium rounded-lg transition-colors cursor-pointer ${
                  !isCompleted
                    ? 'bg-[var(--bg-primary)] text-[var(--text-muted)] cursor-not-allowed'
                    : isSelected
                    ? 'bg-[var(--error)] text-white hover:bg-[var(--error)]/80'
                    : 'bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)]'
                }`}
              >
                {!isCompleted ? 'Not Ready' : isSelected ? 'Deselect' : 'Select'}
              </button>
            </div>

            {/* Use in Collage button */}
            <button
              onClick={() => {
                const params = new URLSearchParams();
                params.set('prompt', encodeURIComponent(currentPrompt.prompt));
                if (seriesId) params.set('series_id', seriesId.toString());
                router.push(`/collage?${params.toString()}`);
              }}
              className="w-full px-4 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors cursor-pointer text-sm text-[var(--text-secondary)] flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              Use in Collage
            </button>
          </div>

          {/* Down Arrow - Navigation */}
          <button
            onClick={goToNext}
            disabled={detailIndex === safePrompts.length - 1}
            className={`
              relative z-10 flex items-center justify-center py-4 border-t border-[var(--border-subtle)]
              transition-colors cursor-pointer
              ${detailIndex === safePrompts.length - 1
                ? 'text-[var(--text-muted)] opacity-30 cursor-not-allowed'
                : 'text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-primary)]'}
            `}
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <span className="ml-2 text-sm font-medium">Next ({detailIndex + 2}/{safePrompts.length})</span>
          </button>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="space-y-4">
      {/* Header with progress and selection controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {isPolling && processingCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
              <svg className="w-4 h-4 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Generating {completedCount}/{safePrompts.length}</span>
            </div>
          )}
          {!isPolling && safePrompts.length > 0 && completedCount === safePrompts.length && (
            <span className="text-sm text-[var(--success)]">
              All {safePrompts.length} previews ready
            </span>
          )}
          {failedCount > 0 && (
            <span className="text-sm text-[var(--error)]">
              {failedCount} failed
            </span>
          )}
        </div>

        {/* Selection controls */}
        {completedCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--text-muted)]">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={onSelectAll}
              className="text-sm text-[var(--accent)] hover:underline cursor-pointer"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={onDeselectAll}
              className="text-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)] cursor-pointer"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Preview grid - 5 columns */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {safePrompts.map((prompt, index) => (
          <VariantPreviewCard
            key={prompt.id}
            prompt={prompt}
            isSelected={selectedIds.has(prompt.id)}
            onToggleSelect={onToggleSelect}
            onEnlarge={() => setDetailIndex(index)}
          />
        ))}
      </div>

      {/* Hint */}
      {safePrompts.length > 0 && (
        <p className="text-xs text-[var(--text-muted)] text-center">
          Click a preview to enlarge. Use arrow keys to navigate. Press Space to select.
        </p>
      )}
    </div>
  );
}
